import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import Select, { components } from "react-select";
import TruncatedOption from "components/truncatedOption";
import { customStyleForMultiSelect } from "styles/reactSelectStyle";
import { ReactComponent as SearchIcon } from "images/profile/search-normal.svg";
import { ReactComponent as DeselectIcon } from "images/profile/deselectIcon.svg";
import { ReactComponent as SelectedIcon } from "images/profile/selectIcon.svg";
import { ReactComponent as DefaultSelection } from "images/profile/defaultChecboxSeletection.svg";
const { MenuList, ValueContainer, SingleValue, Placeholder } = components;

const Option = (props) => {
  let isSelectAll = props.isSelectAll && props.hasValue;

  let isSelected = props.isSelected;

  return (
    <div>
      <components.Option {...props}>
        <div className="flex gap-[8px] object-fill">
          {props.value === "selectAll" ? (
            isSelectAll ? (
              <DeselectIcon className="w=[20px] h-[21px]" />
            ) : (
              <DefaultSelection className="w=[20px] h-[21px]" />
            )
          ) : isSelected ? (
            <SelectedIcon className="w=[20px] h-[21px]" />
          ) : (
            <DefaultSelection className="w=[20px] h-[21px]" />
          )}
          <div>{props.label}</div>
        </div>
      </components.Option>
    </div>
  );
};

const CustomMenuList = ({ selectProps, ...props }) => {
  const { onInputChange, inputValue, onMenuInputFocus } = selectProps;

  // Copied from source
  const ariaAttributes = {
    "aria-autocomplete": "list",
    "aria-label": selectProps["aria-label"],
    "aria-labelledby": selectProps["aria-labelledby"],
  };
  return (
    <>
      <div className="pt-[16px] px-[16px] relative  z-50">
        <SearchIcon className="absolute top-[25px] left-[29px]" />
        <input
          type="text"
          placeholder="Search"
          autoCorrect="off"
          autoComplete="off"
          spellCheck="false"
          value={inputValue}
          onChange={(e) =>
            onInputChange(e.currentTarget.value, {
              action: "input-change",
            })
          }
          className={`flex h-[43px] w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${"border-default-color"} bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] pr-[14px] pl-[40px]`}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.target.focus();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.target.focus();
          }}
          onFocus={onMenuInputFocus}
          {...ariaAttributes}
        />
      </div>
      <MenuList {...props} selectProps={selectProps} />
    </>
  );
};

const CustomValueContainer = ({ children, selectProps, ...props }) => {
  let [options, input] = children;

  if (options && options.length > 0) {
    const { length } = options;

    if (length > 1) {
      let firstOption = options.shift();
      let others = options.map((el) => {
        return el.props.children.props.label;
      });
      options = [
        firstOption,
        <div
          className="flex p-[6px] gap-[2px] cursor-pointer rounded-[4px] bg-light-primary h-[27px]"
          data-tooltip-id="my-tooltip"
          data-tooltip-content={others.join(", ")}
        >
          <div className="text-primary font-medium text-[14px]">{`+ ${
            length - 1
          }`}</div>
        </div>,
      ];
    }
  }

  const commonProps = {
    cx: props.cx,
    clearValue: props.clearValue,
    getStyles: props.getStyles,
    getValue: props.getValue,
    hasValue: props.hasValue,
    isMulti: props.isMulti,
    isRtl: props.isRtl,
    options: props.options,
    selectOption: props.selectOption,
    setValue: props.setValue,
    selectProps,
    theme: props.theme,
    getClassNames: props.getClassNames,
    ...props,
  };

  return (
    <>
      <ValueContainer {...props} selectProps={selectProps}>
        {React.Children.map(children, (child, index) => {
          return child ? (
            index === children.length - 1 && (
              <>
                {options}
                {input}
              </>
            )
          ) : props.hasValue ? (
            <SingleValue
              {...commonProps}
              isFocused={selectProps.isFocused}
              isDisabled={selectProps.isDisabled}
            >
              {selectProps.getOptionLabel(props.getValue()[0])}
            </SingleValue>
          ) : (
            <Placeholder
              {...commonProps}
              key="placeholder"
              isDisabled={selectProps.isDisabled}
              data={props.getValue()}
            >
              {selectProps.placeholder}
            </Placeholder>
          );
        })}
      </ValueContainer>
      <Tooltip
        id="my-tooltip"
        style={{
          background: "#fff",
          color: "#181819",
          fontSize: 14,
          boxShadow:
            "0px 4px 8px -2px rgba(16, 24, 40, 0.10), 0px 2px 4px -2px rgba(16, 24, 40, 0.06)",
          opacity: 1,
          fontFamily: "urwgeometric-bold",
          borderRadius: 4,
        }}
      />
    </>
  );
};

function SelectComponent({
  label = "",
  isRequired = false,
  errorMessage = "",
  name = "",
  register = () => {},
  registerArgs = {},
  containerClassName = "",
  labelClassName = "",
  maxMenuHeight = "10rem",
  isDisabled = false,
  options = [],
  onChange = () => {},
  isMulti = false,
  value = null,
  placeholder = "Search...",
  closeMenuOnSelect = true,
  showError = false,
  isClearable = false,
  withCheckBox = false,
}) {
  const [textSearch, setTextSearch] = useState("");

  const [isSelectAll, setIsSelectAll] = useState(
    withCheckBox &&
      value &&
      Array.isArray(value) &&
      value?.length > 1 &&
      value.length === options.length
      ? true
      : false
  );
  const containerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const selectOptions = withCheckBox
    ? [{ value: "selectAll", label: "All", isAllSelected: false }, ...options]
    : options;

  let customComponents = {
    MenuList: CustomMenuList,
    ValueContainer: CustomValueContainer,
  };

  if (withCheckBox) {
    customComponents.Option = (props) => (
      <Option {...props} isSelectAll={isSelectAll} />
    );
  }

  const onDomClick = (e) => {
    let menu = containerRef.current.querySelector(".selectMulti__menu");

    if (
      !containerRef.current.contains(e.target) ||
      !menu ||
      !menu.contains(e.target)
    ) {
      setIsFocused(false);
      setTextSearch("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", onDomClick);

    return () => {
      document.removeEventListener("mousedown", onDomClick);
      setIsSelectAll(false);
    };
  }, []);

  return (
    <div
      className={
        containerClassName ? containerClassName : "flex flex-col gap-[10px]"
      }
    >
      {label && (
        <div
          className={
            labelClassName
              ? labelClassName
              : "text-primary font-semibold text-[16px]"
          }
        >
          {label} {isRequired && <span className="text-red">*</span>}
        </div>
      )}
      <div
        className={`grid w-full ${
          errorMessage &&
          isRequired &&
          (!value || showError || (Array.isArray(value) && value.length <= 0))
            ? "border-red"
            : "border-default-color"
        } border-solid border-[1px] rounded-[4px]`}
        ref={containerRef}
      >
        <Select
          classNamePrefix="selectMulti"
          name={name}
          {...register(name, { ...registerArgs })}
          value={value ? value : isMulti ? [] : ""}
          inputValue={textSearch}
          maxMenuHeight={maxMenuHeight}
          placeholder={placeholder}
          isClearable={isClearable}
          isDisabled={isDisabled}
          options={selectOptions}
          hideSelectedOptions={false}
          isSearchable={false}
          isOptionDisabled={(option) => option.isDisabled}
          components={{
            ...customComponents,
          }}
          onChange={(e) => {
            setIsFocused(false);
            if (withCheckBox) {
              if (e && e.some((option) => option.value === "selectAll")) {
                if (e.length - 1 === options.length) {
                  // Deselect all if all options are selected
                  setIsSelectAll(false);
                  onChange([]);
                } else {
                  setIsSelectAll(true);
                  // Select all
                  onChange(options);
                }
              } else {
                setIsSelectAll(false);
                onChange(e || []);
              }
            } else {
              const event = !Array.isArray(e) && !e ? "" : e;
              // eslint-disable-next-line no-unused-expressions
              typeof onChange === "function" ? onChange(event) : null;
            }
          }}
          isMulti={isMulti}
          closeMenuOnSelect={closeMenuOnSelect}
          formatOptionLabel={({ label }) => <TruncatedOption label={label} />}
          styles={customStyleForMultiSelect}
          onMenuInputFocus={() => {
            setIsFocused(true);
          }}
          onInputChange={(val) => {
            setTextSearch(val);
          }}
          menuPortalTarget={document.body}
          {...{
            menuIsOpen: isFocused || undefined,
            isFocused: isFocused || undefined,
          }}
        />
      </div>
      {errorMessage &&
        isRequired &&
        (!value ||
          showError ||
          (Array.isArray(value) && value.length <= 0)) && (
          <span className="text-[12px] text-red font-regular">
            {errorMessage}
          </span>
        )}
    </div>
  );
}

SelectComponent.propTypes = {
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  errorMessage: PropTypes.string,
  name: PropTypes.string,
  register: PropTypes.func,
  registerArgs: PropTypes.object,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  maxMenuHeight: PropTypes.string,
  isDisabled: PropTypes.bool,
  options: PropTypes.array,
  onChange: PropTypes.func,
  selectClassName: PropTypes.string,
  isMulti: PropTypes.bool,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  closeMenuOnSelect: PropTypes.bool,
  showError: PropTypes.bool,
  isClearable: PropTypes.bool,
};

export default SelectComponent;
